import React, { useEffect } from "react";
import { HashRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Your imports
import Dashboard from "./pages/Dashboard";
import TablePage from "./pages/TablePage";
import OrderPage from "./pages/OrderPage";
import LoginPage from "./pages/LoginPage";
import SignupPage from "./pages/SignupPage";
import AddTablePage from "./pages/AddTablePage";
import AddCategory from "./pages/AddCategory";
import AddProduct from "./pages/AddProduct";
import PlanShowcase from "./pages/PlanShowcase";
import POSSettingsPage from "./pages/POSSettingsPage";
import ConnectBluetoothPage from "./pages/ConnectBluetoothScreen";
import SalesReportPage from "./pages/SalesReportPage";
import SalesListPage from "./pages/SalesListPage";
import ReceiptPage from "./pages/ReceiptPage";
import LayoutWithDrawer from "./components/LayoutWithDrawer";
import AddPartyPage from "./pages/AddPartyPage";
import AddWaiterPage from "./pages/AddWaiterPage";
import ProfilePage from "./pages/ProfilePage";
import LinkedRestaurantsPage from "./pages/LinkedRestaurantsPage";
import PendingScreen from "./components/PendingScreen";
import QRManagementPage from "./pages/QRManagementPage";
import QrTableOrderPage from "./pages/QrTableOrderPage";
import StockInPage from "./pages/StockInPage";
import InventoryUsagePage from "./pages/InventoryUsagePage";
import FinanceTrackerPage from "./pages/FinanceTrackerPage";
import KhatabookPage from "./pages/KhatabookPage";
import PartyBillsPage from "./pages/PartyBillsPage";
import MenuScannerPage from "./pages/MenuScannerPage";
import KOTManagementPage from "./pages/KOTManagementPage";
import MonthlySummaryPage from "./pages/MonthlySummaryPage";
import TopProductsPage from "./pages/TopProductsPage";
import TopWaitersPage from "./pages/TopWaitersPage";
import HourlySalesPage from "./pages/HourlySalesPage";
import CategoryPerformancePage from "./pages/CategoryPerformancePage";
import TableUtilizationPage from "./pages/TableUtilizationPage";
import FinanceReportPage from "./pages/FinanceReportPage";
import MorePage from "./pages/MorePage";
import PrintScreen from "./pages/PrintScreen";
import HelpCenterPage from "./pages/HelpCenterPage";
import SupportChatPage from "./pages/SupportChatPage";
import FeedbackPage from "./pages/FeedbackPage";
import DeletedOrdersPage from "./pages/DeletedOrdersPage";
import ReportsPage from "./pages/ReportsPage";
import KeyboardShortcutsPage from "./pages/KeyboardShortcutsPage";
import DayWiseItemReportPage from "./pages/DayWiseItemReportPage";
import DayWiseCategoryReportPage from "./pages/DayWiseCategoryReportPage";
import DayWiseKOTReportPage from "./pages/DayWiseKOTReportPage";
import MonthWiseItemReportPage from "./pages/MonthWiseItemReportPage";
import PaymentMethodReportPage from "./pages/PaymentMethodReportPage";
import BillingHistoryPage from "./pages/BillingHistoryPage";
import BulkGstUpdatePage from "./pages/BulkGstUpdatePage";
import DigitalMenuPage from "./pages/DigitalMenuPage";
import CreditPaymentReportPage from "./pages/CreditPaymentReportPage";
import CustomExcelReportPage from "./pages/CustomExcelReportPage";
import SplitPaymentReportPage from "./pages/SplitPaymentReportPage";
import InventoryStockSummaryReportPage from "./pages/InventoryStockSummaryReportPage";
import InventoryDailyUsageReportPage from "./pages/InventoryDailyUsageReportPage";
import InventoryLowStockReportPage from "./pages/InventoryLowStockReportPage";
import InventoryItemPerformanceReportPage from "./pages/InventoryItemPerformanceReportPage";
import RestaurantsOverviewPage from "./pages/RestaurantsOverviewPage";
import ForgotPasswordScreen from "./pages/ForgotPasswordScreen";
import LoginHelpScreenPage from "./pages/LoginHelpScreenPage";
import PoliciesPage from "./pages/PoliciesPage";
import PrinterSettingsPage from "./pages/PrinterSettingsPage";
import ProductReorderPage from "./pages/ProductReorderPage";
import ReferralPage from "./pages/ReferralPage";
import RetryPrintScreenPage from "./pages/RetryPrintScreenPage";
import RolePermissionsPage from "./pages/RolePermissionsPage";
import AdminImpersonationPage from "./pages/AdminImpersonationPage";
import ResetAccountPage from "./pages/ResetAccountPage";
import { isPrimaryLinkedRestaurantSession } from "./utils/accountLinks";
import {
  clearImpersonationStorage,
  getStoredSession,
  getStoredUser,
  hasWebAccessFromStorage,
  isImpersonating,
  isMasterSession,
} from "./utils/impersonation";
import { syncLogRocketSession } from "./utils/logRocket";
import { canAccessFeature, canAccessRoute, canUseWebLogin } from "./utils/rbac";

const UTM_STORAGE_KEY = "webSignupAttribution";
const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
];

const pickUtmParams = (search = "") => {
  const raw = String(search || "").trim();
  if (!raw) return {};

  const normalized = raw.startsWith("?") ? raw.slice(1) : raw;
  const params = new URLSearchParams(normalized);
  const payload = {};

  UTM_KEYS.forEach((key) => {
    const value = params.get(key);
    if (value) payload[key] = value;
  });

  return payload;
};

const pickUtmParamsFromHash = (hash = "") => {
  const raw = String(hash || "");
  const queryIndex = raw.indexOf("?");
  if (queryIndex < 0) return {};
  return pickUtmParams(raw.slice(queryIndex));
};

const readStoredUtmParams = () => {
  if (typeof window === "undefined") return {};

  try {
    const raw = sessionStorage.getItem(UTM_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return UTM_KEYS.reduce((acc, key) => {
      if (parsed?.[key]) acc[key] = parsed[key];
      return acc;
    }, {});
  } catch (error) {
    return {};
  }
};

const persistUtmParams = (payload) => {
  if (typeof window === "undefined" || !Object.keys(payload || {}).length) return;
  sessionStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(payload));
};

//testing

const ProtectedLayout = ({ children }) => {
  const location = useLocation();
  if (!hasWebAccessFromStorage()) {
    localStorage.removeItem("userData");
    clearImpersonationStorage();
    return <Navigate to="/login" replace />;
  }

  const user = getStoredUser();
  if (!canUseWebLogin(user)) {
    localStorage.removeItem("userData");
    clearImpersonationStorage();
    return <Navigate to="/login" replace />;
  }
  if (isMasterSession(user)) {
    return <Navigate to="/login/master" replace />;
  }
  if (!canAccessRoute(location.pathname, user)) {
    return <Navigate to="/dashboard" replace />;
  }
  return <LayoutWithDrawer>{children}</LayoutWithDrawer>;
};

const MasterOnlyLayout = ({ children }) => {
  if (!hasWebAccessFromStorage()) {
    return <Navigate to="/login" replace />;
  }
  if (!canAccessFeature("impersonation", getStoredUser())) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};

const AdminOnlyLayout = ({ children }) => {
  if (!hasWebAccessFromStorage()) {
    localStorage.removeItem("userData");
    clearImpersonationStorage();
    return <Navigate to="/login" replace />;
  }

  const user = getStoredUser();
  if (!canAccessFeature("impersonation", user) && !isImpersonating()) {
    return <Navigate to="/dashboard" replace />;
  }

  return <LayoutWithDrawer>{children}</LayoutWithDrawer>;
};

const ReportsHubLayout = ({ children }) => {
  const user = getStoredUser();
  if (canAccessFeature("impersonation", user) || isImpersonating()) {
    return <AdminOnlyLayout>{children}</AdminOnlyLayout>;
  }

  return <ProtectedLayout>{children}</ProtectedLayout>;
};

const RestaurantsOverviewLayout = ({ children }) => {
  const user = getStoredUser();
  if (canAccessFeature("impersonation", user) || isImpersonating()) {
    return <AdminOnlyLayout>{children}</AdminOnlyLayout>;
  }

  if (isPrimaryLinkedRestaurantSession()) {
    return <ProtectedLayout>{children}</ProtectedLayout>;
  }

  return <Navigate to="/reports" replace />;
};

const AttributionCapture = () => {
  const location = useLocation();

  useEffect(() => {
    const nextPayload = {
      ...readStoredUtmParams(),
      ...pickUtmParams(typeof window !== "undefined" ? window.location.search : ""),
      ...pickUtmParams(location.search),
      ...pickUtmParamsFromHash(typeof window !== "undefined" ? window.location.hash : ""),
    };

    if (Object.keys(nextPayload).length) {
      persistUtmParams(nextPayload);
    }
  }, [location.hash, location.search]);

  return null;
};

const LogRocketSessionSync = () => {
  const location = useLocation();

  useEffect(() => {
    syncLogRocketSession(getStoredSession());
  }, [location.hash, location.pathname, location.search]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const handleStorage = () => {
      syncLogRocketSession(getStoredSession());
    };

    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  return null;
};

function App() {
  const pendingRoutes = [
    { path: "/more", title: "More", mobileScreen: "MoreScreen" },
    { path: "/feedback-hub", title: "Feedback Hub", mobileScreen: "FeedbackHub" },
    { path: "/finance/tracker", title: "Finance Tracker", mobileScreen: "FinanceTracker" },
    { path: "/finance/add-entry", title: "Add Finance Entry", mobileScreen: "AddFinanceEntry" },
    { path: "/finance/report", title: "Finance Report", mobileScreen: "FinanceReport" },
    { path: "/change-password", title: "Change Password", mobileScreen: "ChangePassword" },
    { path: "/logout", title: "Logout", mobileScreen: "Logout" },
    // { path: "/reports", title: "Reports", mobileScreen: "Reports" },
    { path: "/feedback", title: "Feedback", mobileScreen: "FeedbackScreen" },
    { path: "/waiters", title: "Waiter List", mobileScreen: "WaiterList" },
    { path: "/kot", title: "KOT Screen", mobileScreen: "KOTScreen" },
    { path: "/kot/list", title: "KOT List", mobileScreen: "KOTListScreen" },
    { path: "/kot/management", title: "KOT Management", mobileScreen: "KOTManagement" },
    { path: "/kot/waiter", title: "Waiter KOT", mobileScreen: "WaiterKOTScreen" },
    { path: "/staff", title: "Staff Screen", mobileScreen: "StaffScreen" },
    { path: "/staff/add", title: "Manage Staff", mobileScreen: "AddStaff" },
    { path: "/users/add", title: "Add User", mobileScreen: "AddUser" },
    { path: "/order-done", title: "Order Done", mobileScreen: "OrderDoneScreen" },
    { path: "/profile/edit", title: "Edit Profile", mobileScreen: "EditProfile" },
    // { path: "/sales-reports", title: "Sales Reports", mobileScreen: "SalesReports" },
    { path: "/order/edit", title: "Edit Order", mobileScreen: "EditOrderScreen" },
    { path: "/sales-screen", title: "Sales Screen", mobileScreen: "SalesScreen" },
    { path: "/products", title: "Product List", mobileScreen: "ProductList" },
    { path: "/product/edit", title: "Edit Product", mobileScreen: "EditProduct" },
    // now implemented
    // { path: "/help", title: "Help Center", mobileScreen: "HelpCenterScreen" },
    // { path: "/support", title: "Support Chat", mobileScreen: "SupportChatScreen" },
    { path: "/print", title: "Print Screen", mobileScreen: "PrintScreen" },
    { path: "/monthly-summary", title: "Monthly Summary", mobileScreen: "MonthlySummary" },
    // { path: "/reports/top-waiters", title: "Top Waiters Report", mobileScreen: "TopWaitersReport" },
    // { path: "/reports/top-products", title: "Top Products Report", mobileScreen: "TopProductsReport" },
    // { path: "/reports/hourly", title: "Hourly Sales Report", mobileScreen: "HourlySalesReport" },
    // {
    //   path: "/reports/category-performance",
    //   title: "Category Performance Report",
    //   mobileScreen: "CategoryPerformanceReport",
    // },
    // {
    //   path: "/reports/table-utilization",
    //   title: "Table Utilization Report",
    //   mobileScreen: "TableUtilizationReport",
    // },
    // removed: now real page exists
    { path: "/party-bills", title: "Party Bills", mobileScreen: "PartyBillsScreen" },
    { path: "/qr/table-order", title: "QR Table Order", mobileScreen: "QrTableOrder" },
    { path: "/notifications", title: "Notifications", mobileScreen: "NotificationScreen" },
    { path: "/calculator", title: "Calculator", mobileScreen: "Calculator" },
    // { path: "/plan-selection", title: "Plan Selection", mobileScreen: "PlanSelectionScreen" },
    { path: "/stock-movement", title: "Stock Movement", mobileScreen: "StockMovementScreen" },
    { path: "/how-to-use", title: "How To Use", mobileScreen: "HowToUse" },
    { path: "/khatabook", title: "Khatabook", mobileScreen: "Khatabook" },
    // { path: "/reports/day-wise-items", title: "Day Wise Item Report", mobileScreen: "DayWiseItemReport" },
    // { path: "/reports/payment-method", title: "Payment Method Report", mobileScreen: "PaymentMethodReport" },
    { path: "/find-us", title: "Find Us On", mobileScreen: "FindUsOn" },
    { path: "/waiter/profile", title: "Waiter Profile", mobileScreen: "WaiterProfile" },
    { path: "/chef/dashboard", title: "Chef Dashboard", mobileScreen: "ChefDashboard" },
    { path: "/chef/profile", title: "Chef Profile", mobileScreen: "ChefProfile" },
    { path: "/qr/dashboard", title: "QR Code Dashboard", mobileScreen: "QrCodeDashboard" },
    // Public/onboarding style routes
    { path: "/onboarding", title: "Onboarding", mobileScreen: "OnboardingScreen", layout: false },
    { path: "/onboarding/menu-scanner", title: "Menu Scanner Onboarding", mobileScreen: "MenuScannerOB", layout: false },
    { path: "/onboarding/add-product", title: "Onboarding Add Product", mobileScreen: "AddProductOB", layout: false },
    { path: "/onboarding/add-category", title: "Onboarding Add Category", mobileScreen: "AddCategoryOB", layout: false },
  ];

  const renderPendingRoutes = () =>
    pendingRoutes.map(({ path, title, mobileScreen, layout = true }) => {
      const screen = <PendingScreen title={title} mobileScreen={mobileScreen} />;
      return (
        <Route
          key={path}
          path={path}
          element={layout ? <ProtectedLayout>{screen}</ProtectedLayout> : screen}
        />
      );
    });

  return (
    <HashRouter>
      <LogRocketSessionSync />
      <AttributionCapture />
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        pauseOnHover
      />

      <Routes>
        {/* Root route: check login */}
        <Route
          path="/"
          element={
            hasWebAccessFromStorage() ? (
              !canUseWebLogin(getStoredUser()) ? (
                <Navigate to="/login" />
              ) : canAccessFeature("impersonation", getStoredUser()) ? (
                <Navigate to="/login/master" />
              ) : (
                <Navigate to="/dashboard" />
              )
            ) : (
              <Navigate to="/login" />
            )
          }
        />

        {/* Public */}
        <Route path="/login" element={<LoginPage />} />
        <Route
          path="/login/master"
          element={
            <MasterOnlyLayout>
              <AdminImpersonationPage />
            </MasterOnlyLayout>
          }
        />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordScreen />} />

        {/* Protected */}
        <Route
          path="/dashboard"
          element={
            <ProtectedLayout>
              <Dashboard />
            </ProtectedLayout>
          }
        />
        <Route
          path="/tables"
          element={
            <ProtectedLayout>
              <TablePage />
            </ProtectedLayout>
          }
        />
        <Route
          path="/order/:tableId"
          element={
            <ProtectedLayout>
              <OrderPage />
            </ProtectedLayout>
          }
        />
        <Route
          path="/connect-bluetooth"
          element={
            <ProtectedLayout>
              <ConnectBluetoothPage />
            </ProtectedLayout>
          }
        />
        <Route
          path="/sales-report"
          element={
            <ProtectedLayout>
              <SalesReportPage />
            </ProtectedLayout>
          }
        />
        <Route
          path="/sales"
          element={
            <ProtectedLayout>
              <SalesListPage />
            </ProtectedLayout>
          }
        />
        <Route
          path="/receipt/:orderId"
          element={
            <ProtectedLayout>
              <ReceiptPage />
            </ProtectedLayout>
          }
        />
        <Route
          path="/print/:orderId"
          element={
            <ProtectedLayout>
              <PrintScreen />
            </ProtectedLayout>
          }
        />
        <Route
          path="/add-table"
          element={
            <ProtectedLayout>
              <AddTablePage />
            </ProtectedLayout>
          }
        />
        <Route
          path="/add-category"
          element={
            <ProtectedLayout>
              <AddCategory />
            </ProtectedLayout>
          }
        />
        <Route
          path="/add-product"
          element={
            <ProtectedLayout>
              <AddProduct />
            </ProtectedLayout>
          }
        />
        <Route
          path="/plan-selection"
          element={
            <ProtectedLayout>
              <PlanShowcase />
            </ProtectedLayout>
          }
        />
        <Route
          path="/pos-settings"
          element={
            <ProtectedLayout>
              <POSSettingsPage />
            </ProtectedLayout>
          }
        />
        <Route
          path="/add-party"
          element={
            <ProtectedLayout>
              <AddPartyPage />
            </ProtectedLayout>
          }
        />
        <Route
          path="/staff/manage"
          element={
            <ProtectedLayout>
              <AddWaiterPage />
            </ProtectedLayout>
          }
        />
        <Route path="/add-waiter" element={<Navigate to="/staff/manage" replace />} />
        <Route
          path="/profile"
          element={
            <ProtectedLayout>
              <ProfilePage />
            </ProtectedLayout>
          }
        />
        <Route
          path="/linked-restaurants"
          element={
            <ProtectedLayout>
              <LinkedRestaurantsPage />
            </ProtectedLayout>
          }
        />
        <Route
          path="/help"
          element={
            <ProtectedLayout>
              <HelpCenterPage />
            </ProtectedLayout>
          }
        />
        <Route
          path="/keyboard-shortcuts"
          element={
            <ProtectedLayout>
              <KeyboardShortcutsPage />
            </ProtectedLayout>
          }
        />
        <Route
          path="/support"
          element={
            <ProtectedLayout>
              <SupportChatPage />
            </ProtectedLayout>
          }
        />
        <Route
          path="/feedback"
          element={
            <ProtectedLayout>
              <FeedbackPage />
            </ProtectedLayout>
          }
        />
        <Route
          path="/qr/management"
          element={
            <ProtectedLayout>
              <QRManagementPage />
            </ProtectedLayout>
          }
        />
        <Route
          path="/qr/table-order"
          element={
            <ProtectedLayout>
              <QrTableOrderPage />
            </ProtectedLayout>
          }
        />
        <Route
          path="/inventory/stock-in"
          element={
            <ProtectedLayout>
              <StockInPage />
            </ProtectedLayout>
          }
        />
        <Route
          path="/inventory/usage"
          element={
            <ProtectedLayout>
              <InventoryUsagePage />
            </ProtectedLayout>
          }
        />
        <Route
          path="/finance/tracker"
          element={
            <ProtectedLayout>
              <FinanceTrackerPage />
            </ProtectedLayout>
          }
        />
        <Route
          path="/khatabook"
          element={
            <ProtectedLayout>
              <KhatabookPage />
            </ProtectedLayout>
          }
        />
        <Route
          path="/party-bills"
          element={
            <ProtectedLayout>
              <PartyBillsPage />
            </ProtectedLayout>
          }
        />
        <Route
          path="/menu-scanner"
          element={
            <ProtectedLayout>
              <MenuScannerPage />
            </ProtectedLayout>
          }
        />
        <Route
          path="/kot/management"
          element={
            <ProtectedLayout>
              <KOTManagementPage />
            </ProtectedLayout>
          }
        />
        <Route
          path="/monthly-summary"
          element={
            <ProtectedLayout>
              <MonthlySummaryPage />
            </ProtectedLayout>
          }
        />
        <Route
          path="/more"
          element={
            <ProtectedLayout>
              <MorePage />
            </ProtectedLayout>
          }
        />
        <Route
          path="/deleted-orders"
          element={
            <ProtectedLayout>
              <DeletedOrdersPage />
            </ProtectedLayout>
          }
        />
        <Route
          path="/billing/history"
          element={
            <ProtectedLayout>
              <BillingHistoryPage />
            </ProtectedLayout>
          }
        />
        <Route
          path="/bulk-gst"
          element={
            <ProtectedLayout>
              <BulkGstUpdatePage />
            </ProtectedLayout>
          }
        />
        <Route
          path="/digital-menu"
          element={
            <ProtectedLayout>
              <DigitalMenuPage />
            </ProtectedLayout>
          }
        />
        <Route
          path="/reports"
          element={
            <ReportsHubLayout>
              <ReportsPage />
            </ReportsHubLayout>
          }
        />
        <Route
          path="/reports/restaurants-overview"
          element={
            <RestaurantsOverviewLayout>
              <RestaurantsOverviewPage />
            </RestaurantsOverviewLayout>
          }
        />
        <Route
          path="/sales-reports"
          element={
            <ProtectedLayout>
              <SalesReportPage />
            </ProtectedLayout>
          }
        />
        <Route
          path="/reports/day-wise-items"
          element={
            <ProtectedLayout>
              <DayWiseItemReportPage />
            </ProtectedLayout>
          }
        />
        <Route
          path="/reports/day-wise-category"
          element={
            <ProtectedLayout>
              <DayWiseCategoryReportPage />
            </ProtectedLayout>
          }
        />
        <Route
          path="/reports/day-wise-kot"
          element={
            <ProtectedLayout>
              <DayWiseKOTReportPage />
            </ProtectedLayout>
          }
        />
        <Route
          path="/reports/month-wise-items"
          element={
            <ProtectedLayout>
              <MonthWiseItemReportPage />
            </ProtectedLayout>
          }
        />
        <Route
          path="/reports/payment-method"
          element={
            <ProtectedLayout>
              <PaymentMethodReportPage />
            </ProtectedLayout>
          }
        />
        <Route
          path="/reports/credit-payment"
          element={
            <ProtectedLayout>
              <CreditPaymentReportPage />
            </ProtectedLayout>
          }
        />
        <Route
          path="/reports/split-payment"
          element={
            <ProtectedLayout>
              <SplitPaymentReportPage />
            </ProtectedLayout>
          }
        />
        <Route
          path="/reports/custom-excel"
          element={
            <ProtectedLayout>
              <CustomExcelReportPage />
            </ProtectedLayout>
          }
        />
        <Route
          path="/reports/top-products"
          element={
            <ProtectedLayout>
              <TopProductsPage />
            </ProtectedLayout>
          }
        />
        <Route
          path="/reports/top-waiters"
          element={
            <ProtectedLayout>
              <TopWaitersPage />
            </ProtectedLayout>
          }
        />
        <Route
          path="/reports/hourly-sales"
          element={
            <ProtectedLayout>
              <HourlySalesPage />
            </ProtectedLayout>
          }
        />
        <Route
          path="/reports/hourly"
          element={
            <ProtectedLayout>
              <HourlySalesPage />
            </ProtectedLayout>
          }
        />
        <Route
          path="/reports/category-performance"
          element={
            <ProtectedLayout>
              <CategoryPerformancePage />
            </ProtectedLayout>
          }
        />
        <Route
          path="/reports/table-utilization"
          element={
            <ProtectedLayout>
              <TableUtilizationPage />
            </ProtectedLayout>
          }
        />
        <Route
          path="/finance/report"
          element={
            <ProtectedLayout>
              <FinanceReportPage />
            </ProtectedLayout>
          }
        />
        <Route
          path="/reports/inventory/stock-summary"
          element={
            <ProtectedLayout>
              <InventoryStockSummaryReportPage />
            </ProtectedLayout>
          }
        />
        <Route
          path="/reports/inventory/daily-usage"
          element={
            <ProtectedLayout>
              <InventoryDailyUsageReportPage />
            </ProtectedLayout>
          }
        />
        <Route
          path="/reports/inventory/low-stock"
          element={
            <ProtectedLayout>
              <InventoryLowStockReportPage />
            </ProtectedLayout>
          }
        />
        <Route
          path="/reports/inventory/item-performance"
          element={
            <ProtectedLayout>
              <InventoryItemPerformanceReportPage />
            </ProtectedLayout>
          }
        />
        <Route path="/login-help" element={<LoginHelpScreenPage />} />
        <Route
          path="/policies"
          element={
            <ProtectedLayout>
              <PoliciesPage />
            </ProtectedLayout>
          }
        />
        <Route
          path="/printer-settings"
          element={
            <ProtectedLayout>
              <PrinterSettingsPage />
            </ProtectedLayout>
          }
        />
        <Route
          path="/product-reorder"
          element={
            <ProtectedLayout>
              <ProductReorderPage />
            </ProtectedLayout>
          }
        />
        <Route
          path="/referral"
          element={
            <ProtectedLayout>
              <ReferralPage />
            </ProtectedLayout>
          }
        />
        <Route
          path="/retry-print"
          element={
            <ProtectedLayout>
              <RetryPrintScreenPage />
            </ProtectedLayout>
          }
        />
        <Route
          path="/role-permissions"
          element={
            <ProtectedLayout>
              <RolePermissionsPage />
            </ProtectedLayout>
          }
        />
        <Route
          path="/reset-account"
          element={
            <ProtectedLayout>
              <ResetAccountPage />
            </ProtectedLayout>
          }
        />
        <Route
          path="/admin/impersonation"
          element={
            <AdminOnlyLayout>
              <AdminImpersonationPage />
            </AdminOnlyLayout>
          }
        />

        {renderPendingRoutes()}

        {/* Fallback */}
        <Route
          path="*"
          element={<div style={{ padding: 30 }}>Page not found</div>}
        />
      </Routes>
    </HashRouter>
  );
}

export default App;
